<script setup lang="ts">
import { parse } from 'qs';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { LocationQueryRaw } from '#vue-router';
import type ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { ReserveType } from '@utils/itinerary';
import { type ReserveInfo } from '@utils/types';

const itineraryStore = useItineraryStore();
const siteConfigs = useSiteConfigs();
const gtm = useGtm();
const router = useRouter();
const selectedOption = ref<ItineraryPackageSet>();
const options = computed(() => itineraryStore.alternativePackageSets);
const currentModule = useCurrentItineraryModule();
const { srpImageAlt } = moduleToRefs(currentModule.value);
const searchFormStore = useSearchFormStore();
const packageSelectStore = usePackageSelectStore();
const layoutStore = useLayoutStore();

const reserveList = computed<ReserveInfo[]>(() => {
    const reserves = siteConfigs.reserves as ReserveInfo[];
    if (reserves) {
        return itineraryStore.reserveInfoList.filter((reserve: ReserveInfo) =>
            reserves.map(r => r.type).includes(reserve.type)
        );
    } else {
        return itineraryStore.reserveInfoList;
    }
});
const reserveInfoList = computed<
    (ReserveInfo & {
        icon?: IconDefinition;
        isLoading: boolean;
        isDisabled: boolean;
        isSelected: boolean;
    })[]
>(() =>
    reserveList.value.map(reserve => ({
        ...reserve,
        icon: searchFormStore.getFormModule(reserve.type).reserveInfo.icon,
        isLoading: isLoading(reserve),
        isDisabled:
            optionOf(reserve) === null &&
            reserve.type !== currentReserveType.value,
        isSelected: reserve.type === currentReserveType.value,
    }))
);
const currentReserveType = computed(() => searchFormStore.reserveType);
const load = (reserveInfo: ReserveInfo) => {
    if (currentReserveType.value === reserveInfo.type) {
        return;
    }
    const option = optionOf(reserveInfo);
    if (!option) {
        return;
    }
    selectedOption.value = option;
    searchFormStore.shareItineraryInfo(reserveInfo.type);
    searchFormStore.setReserveType(reserveInfo.type);
    const [path, query] = option.url!.split('?');
    router.replace({
        path,
        query: parse(query) as LocationQueryRaw,
    });
    gtm?.trackEvent({
        event: `srp-pageview-change to ${reserveInfo.type}${
            reserveInfo.type === ReserveType.Flight ? '-d' : ''
        }`,
    });
};
const optionOf = (reserveInfo: ReserveInfo) =>
    options.value?.[reserveInfo.type];

const isLoading = (reserveInfo: ReserveInfo) => {
    if (currentReserveType.value === reserveInfo.type) {
        return [LoadingStatus.Loading, LoadingStatus.None].includes(
            optionOf(reserveInfo)?.loadingStatus || LoadingStatus.None
        );
    }
    return optionOf(reserveInfo) === undefined;
};

const back = () => {
    if (packageSelectStore.selectedPackages.length) {
        itineraryStore.setPackageSetIndex(0);
        packageSelectStore.clearSelectedPackages();
    } else {
        router.push('/');
    }
};
</script>

<template>
    <div class="suggestions-container">
        <img
            v-if="layoutStore.showSuggestedBg"
            class="image-background"
            :alt="srpImageAlt ?? 'مِستربلیط'"
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAwAAAADICAYAAABf0obKAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAQESURBVHgB7ddBAQAQAAAxFFBaah+C3NZic5/7BgAAkLAGAACQIQAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQIgAAABAiAAAAECIAAAAQ8gFHNgQcV0aoRAAAAABJRU5ErkJggg=="
        />
        <div class="suggestions-list">
            <transition-group name="list">
                <mr-button
                    key="back-button-mobile"
                    :icon-button="{ icon: faArrowRight, color: 'white' }"
                    size="medium"
                    variant="transparent"
                    class="mobile-only"
                    @click.stop="back"
                />
                <div
                    v-for="reserveInfo in reserveInfoList"
                    :key="reserveInfo.type"
                    class="suggestion"
                    :class="{
                        selected: reserveInfo.isSelected,
                        disabled: reserveInfo.isDisabled,
                        loading: reserveInfo.isLoading,
                    }"
                    data-loaded="true"
                    @click="load(reserveInfo)"
                >
                    <div class="content-wrapper">
                        <div class="icon-wrapper">
                            <mr-icon
                                v-if="reserveInfo.icon"
                                class="icon"
                                :icon="reserveInfo.icon"
                                :type="
                                    reserveInfo.isSelected ? 'solid' : 'regular'
                                "
                                :size="{ desktop: 'medium', mobile: 'small' }"
                            />
                        </div>
                        <div class="title">
                            {{ reserveInfo.title }}
                        </div>
                    </div>
                    <div class="selected-sok">
                        <img alt="sok" src="~/assets/files/selected.svg" />
                    </div>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<style scoped lang="scss">
$container-height: 52px;

.suggestions-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: usePalette();
    height: $container-height;
    @include mobile-only {
        height: 56px;
        padding: 12px 4px 0 8px;
    }
    .image-background {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        max-width: 100%;
        height: 350px;
        @include mobile-only {
            display: none;
        }
    }
    .suggestions-list {
        position: relative;
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        height: $container-height;
        max-width: $app-container-max-width;
        width: 100%;
        @include f-gap(4px);
        @include mobile-only {
            overflow-x: scroll;
            height: 56px;
            @include no-scrollbar;
        }
        .suggestion {
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            height: 52px;
            padding: 0 12px;
            cursor: pointer;
            opacity: 0.6;
            @include mobile-only {
                width: 61px;
                padding: 0 8px;
                height: 44px;
            }
            .content-wrapper {
                display: flex;
                align-items: flex-start;
                @include mobile-only {
                    flex-direction: column;
                    align-items: center;
                }
            }
            .selected-sok {
                animation: none !important;
                display: none;
                width: fit-content;
            }
            &.selected {
                .selected-sok {
                    display: flex;
                    @include mobile-only {
                        display: none;
                    }
                }
                opacity: 1;
            }
            .icon-wrapper {
                height: 32px;
                display: flex;
                align-items: center;
                @include mobile-only {
                    height: 20px;
                }
            }
            &.selected,
            &:hover {
                .icon {
                    color: white;
                }
                .title {
                    // text
                    color: white;
                }
            }

            &.disabled {
                opacity: 0.3;
                cursor: not-allowed;
                .title {
                    text-decoration: line-through;
                }
            }

            &.loading {
                & > * {
                    animation: loading 1500ms infinite;
                }
            }
            @keyframes loading {
                0% {
                    opacity: 0.1;
                }
                50% {
                    opacity: 1;
                }
                100% {
                    opacity: 0.1;
                }
            }

            .icon {
                margin-left: 4px;
                color: usePalette($p-blue, $p-200);
            }
            .title {
                // text
                @include tg-l($w7, usePalette($p-blue, $p-200));
                @include mobile-only {
                    @include tg-s($w5, usePalette($p-blue, $p-200));
                    white-space: nowrap;
                }
            }
        }
    }
}

.list-leave-to {
    animation: 0.2s translateY-animation ease reverse;
}

.list-enter-to {
    animation: 0.2 translateY-animation ease;
}

@keyframes translateY-animation {
    from {
        opacity: 0;
        transform: translateY(50%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
